import React, { useState } from 'react'
import PropTypes from 'prop-types'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import Heading from '../Heading/Heading'
import HeadingSizeEnum from '../../enums/headingSize'
import UrlFactory from '../../utils/UrlFactory'

import {
  StyledClientContainer,
  StyledClientLink,
  StyledClientsBanner,
  StyledClientsList,
  StyledHoverText,
  StyledLegalNotice,
  StyledNextArrow,
  StyledPrevArrow,
} from './ClientsBanner.styled'
import NormalizeMedia from '../../utils/NormalizeMedia'
import MouseEvent from '../../enums/MouseEvent'
import useMediaQuery from '../../hooks/useMediaQuery'
import { deviceMax } from '../../theme/breakpoints'

const AUTOPLAY = false
const DOTS = false

const SLIDES_TO_SCROLL = 1
const SLIDES_PER_ROW = 1
const DRAGGABLE = true
const VARIABLEWIDTH = true

const PREV_ARROW = ({ currentSlide, slideCount, ...props }) => (
  <StyledPrevArrow {...props} />
)
const NEXT_ARROW = ({ currentSlide, slideCount, ...props }) => (
  <StyledNextArrow {...props} />
)

const ClientsBanner = ({ className, clients, title, msci }) => {
  const [clientsRef, setClientsRef] = useState(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [hoverText, setHoverText] = useState(null)
  const [isHover, setIsHover] = useState(false)
  const [offset, setOffset] = useState(null)

  const isMobile = useMediaQuery(deviceMax.mobileL)
  const isLaptop = useMediaQuery(deviceMax.laptop)

  const sliderSettings = {
    afterChange: (slideIndex) => setCurrentSlide(slideIndex),
    arrows: !isMobile,
    autoplay: AUTOPLAY,
    dots: DOTS,
    infinite: clients.length >= 5 || isMobile,
    slidesToScroll: SLIDES_TO_SCROLL,
    slidesToShow: isMobile ? 1.5 : isLaptop ? 4 : 5,
    draggable: DRAGGABLE,
    variableWidth: VARIABLEWIDTH,
    slidesPerRow: SLIDES_PER_ROW,
    prevArrow: <PREV_ARROW />,
    nextArrow: <NEXT_ARROW />,
  }

  const showTextOnHover = () => {
    let text =
      hoverText?.logo?.cloudinaryAssetMobile?.[0].context?.custom?.caption ||
      hoverText?.logo?.cloudinaryAssetDesktop?.[0].context?.custom?.caption ||
      hoverText?.textOnHover?.textOnHover ||
      null
    if (text) {
      return (
        <StyledHoverText $offsetX={offset[0]} $offsetY={offset[1]}>
          {text}
        </StyledHoverText>
      )
    }
    return
  }

  const textOnHover = showTextOnHover()

  const toggleHoverText = (event, client, state) => {
    if (state === MouseEvent.enter) {
      setHoverText(client)
      setIsHover(true)
      setOffset([event.pageX, event.pageY])
    } else {
      setIsHover(false)
      setHoverText(null)
    }
  }

  const clientsList = clients.map((client, index) => {
    const url = UrlFactory.addHttps(client.url)
    return (
      <StyledClientContainer
        onMouseOver={(e) => toggleHoverText(e, client, MouseEvent.enter)}
        onMouseLeave={(e) => toggleHoverText(e, client, MouseEvent.leave)}
        key={index}
      >
        <StyledClientLink href={url} target="_blank">
          {NormalizeMedia(client.logo, isMobile, true, 'styledClientBannerImg', true)}
        </StyledClientLink>
      </StyledClientContainer>
    )
  })

  return (
    <StyledClientsBanner className={className}>
      {title && <Heading label={title} size={HeadingSizeEnum.h3} />}
      {isHover && textOnHover}
      <StyledClientsList ref={setClientsRef} {...sliderSettings}>
        {clientsList}
      </StyledClientsList>
      {msci && (
        <StyledLegalNotice>
          (1) The use by Constellium of any MSCI ESG Research LLC or its
          affiliates (“MSCI”) data, and the use of MSCI logos, trademarks,
          service marks or index names herein, do not constitute a sponsorship,
          endorsement, recommendation, or promotion of Constellium by MSCI. MSCI
          services and data are the property of MSCI or its information
          providers, and are provided ‘as-is’ and without warranty. MSCI names
          and logos are trademarks or service marks of MSCI.
        </StyledLegalNotice>
      )}
    </StyledClientsBanner>
  )
}

ClientsBanner.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  msci: PropTypes.bool,
}

export default ClientsBanner
